// Languages of the free test pages (2026-11-04, Robert: "translate the free
// tests ... for traffic; the app stays English for now"). Only the test pages,
// the /tests hub and the IQ test have language versions; the rest of the site
// is English. English stays at the root (/reaction-time-test); every other
// language lives under its prefix (/es/reaction-time-test, /es/tests).
//
// Adding a language = add an entry here, add messages/<code>/*.json and
// content/tests/<code>/*.md (see hb_docs/tasks/build_tests_i18n_2026-11-04.md),
// the cognitive service's word data for that language, and the code to
// hb_infra's Caddy @website matcher. Language detection (2026-11-04): never by
// IP; only /tests redirects, by Accept-Language, for visitors arriving from
// outside who have not chosen a language (proxy.ts); test pages only offer a
// switch (components/i18n/LanguageSuggestion.tsx). See lib/i18n/preferred.ts.

export const DEFAULT_LOCALE = "en";

export interface LocaleInfo {
  /** Path prefix and message folder ("es"). */
  code: string;
  /** Name in the language itself, for the switcher. */
  name: string;
  /** English name (docs, alt text). */
  englishName: string;
  /** <html lang> and hreflang value (BCP 47; Google reads ISO 639-1 + optional script/region). */
  hreflang: string;
  /** Intl locale for numbers and plural rules. Latin digits everywhere: the games show 0–9. */
  intl: string;
  dir: "ltr" | "rtl";
  /** og:locale */
  ogLocale: string;
}

export const LOCALE_INFO: readonly LocaleInfo[] = [
  { code: "en", name: "English", englishName: "English", hreflang: "en", intl: "en-US", dir: "ltr", ogLocale: "en_US" },
  { code: "es", name: "Español", englishName: "Spanish", hreflang: "es", intl: "es", dir: "ltr", ogLocale: "es_ES" },
  // Brazilian wording that reads naturally in Portugal too (one page for both).
  { code: "pt", name: "Português", englishName: "Portuguese", hreflang: "pt", intl: "pt-BR", dir: "ltr", ogLocale: "pt_BR" },
  { code: "de", name: "Deutsch", englishName: "German", hreflang: "de", intl: "de", dir: "ltr", ogLocale: "de_DE" },
  { code: "fr", name: "Français", englishName: "French", hreflang: "fr", intl: "fr", dir: "ltr", ogLocale: "fr_FR" },
  { code: "it", name: "Italiano", englishName: "Italian", hreflang: "it", intl: "it", dir: "ltr", ogLocale: "it_IT" },
  { code: "ja", name: "日本語", englishName: "Japanese", hreflang: "ja", intl: "ja", dir: "ltr", ogLocale: "ja_JP" },
  { code: "ko", name: "한국어", englishName: "Korean", hreflang: "ko", intl: "ko", dir: "ltr", ogLocale: "ko_KR" },
  { code: "id", name: "Bahasa Indonesia", englishName: "Indonesian", hreflang: "id", intl: "id", dir: "ltr", ogLocale: "id_ID" },
  { code: "tr", name: "Türkçe", englishName: "Turkish", hreflang: "tr", intl: "tr", dir: "ltr", ogLocale: "tr_TR" },
  { code: "pl", name: "Polski", englishName: "Polish", hreflang: "pl", intl: "pl", dir: "ltr", ogLocale: "pl_PL" },
  // Simplified Chinese at /zh; hreflang with the script (Google: zh-Hans = Simplified).
  { code: "zh", name: "简体中文", englishName: "Chinese (Simplified)", hreflang: "zh-Hans", intl: "zh-CN", dir: "ltr", ogLocale: "zh_CN" },
  { code: "zh-hant", name: "繁體中文", englishName: "Chinese (Traditional)", hreflang: "zh-Hant", intl: "zh-TW", dir: "ltr", ogLocale: "zh_TW" },
  { code: "hi", name: "हिन्दी", englishName: "Hindi", hreflang: "hi", intl: "hi-u-nu-latn", dir: "ltr", ogLocale: "hi_IN" },
  { code: "ta", name: "தமிழ்", englishName: "Tamil", hreflang: "ta", intl: "ta-u-nu-latn", dir: "ltr", ogLocale: "ta_IN" },
  { code: "te", name: "తెలుగు", englishName: "Telugu", hreflang: "te", intl: "te-u-nu-latn", dir: "ltr", ogLocale: "te_IN" },
  { code: "mr", name: "मराठी", englishName: "Marathi", hreflang: "mr", intl: "mr-u-nu-latn", dir: "ltr", ogLocale: "mr_IN" },
  { code: "ur", name: "اردو", englishName: "Urdu", hreflang: "ur", intl: "ur-u-nu-latn", dir: "rtl", ogLocale: "ur_PK" },
  { code: "fil", name: "Filipino", englishName: "Filipino", hreflang: "fil", intl: "fil-u-nu-latn", dir: "ltr", ogLocale: "fil_PH" },
  { code: "ar", name: "العربية", englishName: "Arabic", hreflang: "ar", intl: "ar-u-nu-latn", dir: "rtl", ogLocale: "ar_AR" },
  { code: "bn", name: "বাংলা", englishName: "Bengali", hreflang: "bn", intl: "bn-u-nu-latn", dir: "ltr", ogLocale: "bn_BD" },
  { code: "ru", name: "Русский", englishName: "Russian", hreflang: "ru", intl: "ru", dir: "ltr", ogLocale: "ru_RU" },
  // `uk` is the language code for Ukrainian (not the United Kingdom).
  { code: "uk", name: "Українська", englishName: "Ukrainian", hreflang: "uk", intl: "uk", dir: "ltr", ogLocale: "uk_UA" },
  // Serbian in Latin script (most Serbian web search is in Latin; Robert 2026-11-04).
  { code: "sr", name: "Srpski", englishName: "Serbian (Latin)", hreflang: "sr-Latn", intl: "sr-Latn", dir: "ltr", ogLocale: "sr_RS" },
  { code: "vi", name: "Tiếng Việt", englishName: "Vietnamese", hreflang: "vi", intl: "vi-u-nu-latn", dir: "ltr", ogLocale: "vi_VN" },
  { code: "th", name: "ไทย", englishName: "Thai", hreflang: "th", intl: "th-u-nu-latn", dir: "ltr", ogLocale: "th_TH" },
  { code: "fa", name: "فارسی", englishName: "Persian", hreflang: "fa", intl: "fa-u-nu-latn", dir: "rtl", ogLocale: "fa_IR" },
  { code: "nl", name: "Nederlands", englishName: "Dutch", hreflang: "nl", intl: "nl-u-nu-latn", dir: "ltr", ogLocale: "nl_NL" },
  { code: "he", name: "עברית", englishName: "Hebrew", hreflang: "he", intl: "he-u-nu-latn", dir: "rtl", ogLocale: "he_IL" },
  { code: "uz", name: "Oʻzbekcha", englishName: "Uzbek", hreflang: "uz", intl: "uz", dir: "ltr", ogLocale: "uz_UZ" },
  { code: "az", name: "Azərbaycan dili", englishName: "Azerbaijani", hreflang: "az", intl: "az", dir: "ltr", ogLocale: "az_AZ" },
  { code: "kk", name: "Қазақ тілі", englishName: "Kazakh", hreflang: "kk", intl: "kk", dir: "ltr", ogLocale: "kk_KZ" },
  { code: "ne", name: "नेपाली", englishName: "Nepali", hreflang: "ne", intl: "ne-u-nu-latn", dir: "ltr", ogLocale: "ne_NP" },
  { code: "sw", name: "Kiswahili", englishName: "Swahili", hreflang: "sw", intl: "sw", dir: "ltr", ogLocale: "sw_KE" },
];

export type Locale = string;

// Languages being built but not published yet (no pages, no hreflang, not in
// the sitemap, and their messages are not checked). Empty when all are live.
const NOT_LIVE = new Set<string>([]);

/** Every published language code, English first. */
export const LOCALES: readonly string[] = LOCALE_INFO.filter((l) => !NOT_LIVE.has(l.code)).map((l) => l.code);

/** The prefixed languages (all but English). */
export const PREFIXED_LOCALES: readonly string[] = LOCALES.filter((l) => l !== DEFAULT_LOCALE);

export function isLocale(value: unknown): value is Locale {
  return typeof value === "string" && LOCALES.includes(value);
}

export function localeInfo(locale: Locale): LocaleInfo {
  const info = LOCALE_INFO.find((l) => l.code === locale);
  if (!info) throw new Error(`i18n: unknown locale "${locale}"`);
  return info;
}

/** "/reaction-time-test" in `locale`: "/es/reaction-time-test" (English: unchanged). "/tests" works the same way. */
export function localePath(locale: Locale, path: string): string {
  if (locale === DEFAULT_LOCALE) return path;
  return `/${locale}${path === "/" ? "" : path}`;
}

/** Split "/es/stroop-test" into ["es", "/stroop-test"]; English paths give ["en", path]. */
export function splitLocalePath(path: string): [Locale, string] {
  const m = path.match(/^\/([a-z]{2,3}(?:-[a-z]+)?)(\/.*|$)/);
  if (m && m[1] !== DEFAULT_LOCALE && PREFIXED_LOCALES.includes(m[1])) return [m[1], m[2] || "/"];
  return [DEFAULT_LOCALE, path];
}
